import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import PublicBookingForm from '../../components/appointment/PublicBookingForm';
import Logo from '../../components/Logo';
import {
  ChevronDownIcon,
  CalendarIcon,
  ClockIcon,
  HeartIcon,
  StarIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { LogIn, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const testimonials = [
  {
    name: 'Sarah Johnson',
    role: 'Patient',
    content:
      'AvdCare made booking my appointment so easy. The doctors are amazing and the staff is incredibly helpful.',
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=faces&fit=crop&w=80&h=80',
  },
  {
    name: 'Dr. Michael Chen',
    role: 'Cardiologist',
    content:
      'As a healthcare provider, I love how AvdCare streamlines our workflow and improves patient care.',
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1582750433449-648ed127bb54?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
  },
  {
    name: 'Emily Rodriguez',
    role: 'Mother of 3',
    content:
      "Managing my family's health has never been easier. The family registration feature is a game-changer.",
    rating: 5,
    image:
      'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?crop=faces&fit=crop&w=80&h=80',
  },
];

const servicesList = [
  {
    icon: '🫀',
    title: 'Cardiology',
    description:
      'Expert heart care with advanced diagnostic equipment and experienced cardiologists for all cardiac conditions.',
    features: ['ECG & Echocardiography', 'Stress Testing', 'Heart Health Checkups'],
  },
  {
    icon: '🧠',
    title: 'Neurology',
    description:
      'Comprehensive neurological care for conditions affecting the brain, spine, and nervous system.',
    features: ['EEG & EMG', 'Headache Management', 'Stroke Care'],
  },
  {
    icon: '👨‍⚕️',
    title: 'General Medicine',
    description:
      'Primary healthcare services for all ages with experienced physicians for routine and complex care.',
    features: ['Health Checkups', 'Chronic Disease Management', 'Preventive Care'],
  },
  {
    icon: '👶',
    title: 'Pediatrics',
    description: 
      'Specialized healthcare for infants, children, and adolescents with compassionate pediatricians.',
    features: ['Vaccinations', 'Growth Monitoring', 'Child Development'],
  },
  {
    icon: '🦴',
    title: 'Orthopedics',
    description:
      'Expert care for bones, joints, muscles, and musculoskeletal conditions with modern treatment options.',
    features: ['Joint Care', 'Sports Medicine', 'Physiotherapy'],
  },
  {
    icon: '👁️',
    title: 'Ophthalmology',
    description: 
      'Complete eye care services from routine vision exams to advanced surgical treatments.',
    features: ['Eye Exams', 'Cataract Surgery', 'Retinal Care'],
  },
  {
    icon: '🩺',
    title: 'Dermatology',
    description:
      'Expert skin, hair, and nail care with advanced diagnostic and cosmetic dermatology services.',
    features: ['Skin Disorders', 'Cosmetic Treatments', 'Hair Loss Treatment'],
  },
  {
    icon: '👩‍⚕️',
    title: 'Gynecology',
    description:
      "Comprehensive women's health services including preventive care, prenatal, and gynecological treatments.",
    features: ['Prenatal Care', 'Well-Woman Exams', 'Menopause Management'],
  },
];

const doctorsList = [
  {
    name: 'Dr. Sarah Johnson',
    specialty: 'Cardiologist',
    experience: '15+ years',
    image:
      'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 4.9,
  },
  {
    name: 'Dr. Michael Chen',
    specialty: 'Neurologist',
    experience: '12+ years',
    image:
      'https://images.unsplash.com/photo-1537368910025-700350fe46c7?crop=faces&fit=crop&w=300&h=300',
    rating: 4.8,
  },
  {
    name: 'Dr. Emily Rodriguez',
    specialty: 'Pediatrician',
    experience: '10+ years',
    image:
      'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?crop=faces&fit=crop&w=300&h=300',
    rating: 5.0,
  },
  {
    name: 'Dr. James Wilson',
    specialty: 'Orthopedic Surgeon',
    experience: '18+ years',
    image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?q=80&w=764&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 4.9,
  },
];

const LandingPage = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  const yPosAnim = useTransform(scrollYProgress, [0, 0.5], [0, 200]);
  const opacityAnim = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scaleAnim = useSpring(useTransform(scrollYProgress, [0, 0.2], [1, 0.95]), {
    stiffness: 100,
    damping: 30,
  });

  // More subtle floating animation
  const FloatingParticle = ({ delay = 0, duration = 4, xOffset = 0 }) => (
    <motion.div
      className="absolute w-1.5 h-1.5 rounded-full bg-[#9DC9D4]/20"
      animate={{
        y: [0, -20, 0],
        x: [0, xOffset, 0],
        opacity: [0.2, 0.4, 0.2],
      }}
      transition={{
        duration,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
    />
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{fontFamily:'sans-serif'}}>
      <Helmet>
        <title>AvdCare - Book Doctor Appointments Online | Healthcare Management System</title>
        <meta name="description" content="AvdCare is Mumbai's trusted healthcare platform. Book doctor appointments online, access 50+ expert specialists, 24/7 support. Cardiology, Neurology, Pediatrics & more." />
        <meta name="keywords" content="doctor appointment booking, healthcare Mumbai, online doctor consultation, hospital management system, medical appointments, Borivali hospital, AvdCare" />
        <link rel="canonical" href="https://avdcare.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AvdCare - Book Doctor Appointments Online" />
        <meta property="og:description" content="Mumbai's trusted healthcare platform. Book appointments with 50+ expert doctors instantly. 24/7 support available." />
        <meta property="og:url" content="https://avdcare.com/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AvdCare - Book Doctor Appointments Online" />
        <meta name="twitter:description" content="Mumbai's trusted healthcare platform. Book appointments with 50+ expert doctors instantly." />
      </Helmet>
      
    <div className="min-h-screen bg-white text-gray-800 font-sans selection:bg-[#2D869C] selection:text-white overflow-hidden relative">
      {/* Subtle Background Gradient */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#9DC9D4]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#2D869C]/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="fixed top-0 w-full bg-white/95 backdrop-blur-lg border-b border-gray-200 z-50 shadow-sm"
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Logo size="md" showText={true} />
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 font-medium text-gray-700">
              {['services', 'doctors', 'testimonials', 'contact'].map((section) => (
                <motion.button
                  key={section}
                  whileHover={{ y: -2 }}
                  onClick={() => scrollToSection(section)}
                  className="hover:text-[#2D869C] transition-colors focus:outline-none capitalize relative group font-medium"
                >
                  {section}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#2D869C] group-hover:w-full transition-all duration-300" />
                </motion.button>
              ))}
              <div className="flex items-center gap-3 ml-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowBooking(true)}
                  className="bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white px-6 py-2.5 rounded-full hover:shadow-lg transition duration-300 flex items-center gap-2 font-semibold text-sm"
                >
                  <CalendarIcon className="w-4 h-4" />
                  Book Appointment
                </motion.button>
                <Link to="/signin">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-[#2D869C] px-6 py-2.5 rounded-full border border-[#2D869C] hover:bg-[#2D869C]/5 transition duration-300 flex items-center gap-2 font-semibold text-sm"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </motion.button>
                </Link>
              </div>
            </nav>

            {/* Mobile Navigation Buttons */}
            <div className="flex items-center gap-2 lg:hidden">
              <Link to="/signin">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-[#2D869C] p-2 rounded-full hover:bg-gray-100 transition duration-300"
                >
                  <LogIn className="w-5 h-5" />
                </motion.button>
              </Link>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 p-2 rounded-full hover:bg-gray-100 transition duration-300"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-200 shadow-lg"
            >
              <div className="max-w-7xl mx-auto px-4 py-6 space-y-3">
                {['services', 'doctors', 'testimonials', 'contact'].map((section, i) => (
                  <motion.button
                    key={section}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    onClick={() => {
                      scrollToSection(section);
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-5 py-3 text-gray-700 hover:text-[#2D869C] hover:bg-gray-50 rounded-xl transition-all font-medium capitalize"
                  >
                    {section}
                  </motion.button>
                ))}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowBooking(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white px-6 py-3 rounded-xl hover:shadow-lg transition duration-300 flex items-center justify-center gap-2 font-semibold"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    Book Appointment
                  </motion.button>
                  <Link to="/signin" className="block">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full border-2 border-[#2D869C] text-[#2D869C] px-6 py-3 rounded-xl hover:bg-gray-50 transition duration-300 flex items-center justify-center gap-2 font-semibold"
                    >
                      <LogIn className="w-5 h-5" />
                      Login
                    </motion.button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 sm:pb-32 relative overflow-hidden bg-gradient-to-b from-gray-50 to-white"
        aria-label="Welcome to AvdCare Healthcare"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white border border-[#2D869C]/20 rounded-full px-4 py-2 mb-6 shadow-sm"
              >
                <div className="w-2 h-2 bg-[#2D869C] rounded-full animate-pulse" />
                <span className="text-sm font-semibold text-gray-700">
                  Mumbai's Trusted Healthcare Platform
                </span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 leading-[1.1] mb-6">
                Your Health,{' '}
                <span className="text-[#2D869C]">Our Priority</span>
              </h1>
              
              <p className="text-lg text-gray-600 max-w-xl leading-relaxed mb-8">
                Experience world-class healthcare with AvdCare. Book appointments instantly, 
                manage your family's health, and connect with top medical professionals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBooking(true)}
                  className="bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <CalendarIcon className="w-5 h-5" />
                  Book Appointment
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl text-base font-semibold hover:border-[#2D869C] hover:text-[#2D869C] transition-all duration-300"
                >
                  Explore Services
                </motion.button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
                {[
                  { value: '15,000+', label: 'Happy Patients' },
                  { value: '50+', label: 'Expert Doctors' },
                  { value: '24/7', label: 'Support Available' },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-2xl md:text-3xl font-black text-[#2D869C] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[450px] sm:h-[550px] border border-gray-200">
                <img
                  src="https://plus.unsplash.com/premium_photo-1681843026709-ca6a26f1fae8?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="AvdCare professional healthcare team"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

                {/* Floating Info Cards */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute left-4 bottom-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4 max-w-[200px]"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#2D869C]/10 rounded-lg flex items-center justify-center">
                      <ClockIcon className="w-5 h-5 text-[#2D869C]" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm">
                        Quick Booking
                      </div>
                      <div className="text-xs text-gray-600">
                        Under 2 minutes
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute right-4 top-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4"
                >
                  <div className="text-xs text-gray-600 font-semibold mb-1">
                    Next Available
                  </div>
                  <div className="text-lg font-black text-[#2D869C]">
                    Today 2:30 PM
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="py-20 lg:py-24 bg-gray-50"
        aria-label="Our Medical Services"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4">
              <span className="text-[#2D869C] font-semibold text-sm uppercase tracking-wider">
                Our Specialties
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
              Comprehensive Medical Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Expert healthcare across multiple specialties with state-of-the-art facilities 
              and compassionate professionals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesList.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-gray-100 hover:border-[#2D869C]/30 transition-all duration-300 cursor-default"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2D869C] transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-3">
                  {service.description}
                </p>
                
                <ul className="space-y-2 mb-5">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#2D869C] rounded-full mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBooking(true)}
                  className="w-full py-3 text-[#2D869C] border border-[#2D869C] rounded-xl hover:bg-[#2D869C] hover:text-white transition-all duration-300 font-semibold text-sm"
                >
                  Book Consultation
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors Section */}
      <section
        id="doctors"
        className="py-20 lg:py-24 bg-white"
        aria-label="Meet Our Expert Doctors"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4">
              <span className="text-[#2D869C] font-semibold text-sm uppercase tracking-wider">
                Our Medical Team
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
              Meet Our Expert Doctors
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our team of highly qualified medical professionals is dedicated to
              providing exceptional care and expertise.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {doctorsList.map((doctor, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-gray-100 hover:border-[#2D869C]/30 text-center transition-all duration-300"
                tabIndex={0}
                aria-label={`${doctor.name} - ${doctor.specialty}`}
              >
                <div className="relative inline-block mb-5">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-gray-100"
                    loading="lazy"
                  />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#2D869C] rounded-full flex items-center justify-center border-2 border-white">
                    <CheckCircleIcon className="w-5 h-5 text-white" />
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {doctor.name}
                </h3>
                <p className="text-[#2D869C] font-semibold mb-1">
                  {doctor.specialty}
                </p>
                <p className="text-gray-600 text-sm mb-4">{doctor.experience}</p>
                
                <div className="flex justify-center items-center gap-1 mb-5" aria-label={`Rating: ${doctor.rating} stars`}>
                  {[...Array(5)].map((_, idx) => (
                    <StarIcon
                      key={idx}
                      className={`w-4 h-4 ${idx < Math.floor(doctor.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                        }`}
                      aria-hidden="true"
                    />
                  ))}
                  <span className="text-sm text-gray-600 ml-2 font-semibold">({doctor.rating})</span>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBooking(true)}
                  className="w-full bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white py-3 rounded-xl hover:shadow-lg transition duration-300 font-semibold text-sm"
                >
                  Book Appointment
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="testimonials"
        className="py-20 lg:py-24 bg-gray-50"
        aria-label="Patient Testimonials"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4">
              <span className="text-[#2D869C] font-semibold text-sm uppercase tracking-wider">
                Testimonials
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
              What Our Patients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real stories from real patients who have experienced our exceptional care.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl p-10 sm:p-12 shadow-sm border border-gray-200 text-center"
                aria-live="polite"
              >
                <div className="flex justify-center mb-6" aria-label={`${testimonials[currentTestimonial].rating} star rating`}>
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <StarIcon key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-xl text-gray-700 mb-8 leading-relaxed">
                  "{testimonials[currentTestimonial].content}"
                </blockquote>
                
                <div className="flex items-center justify-center gap-4">
                  <img
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].name}
                    className="w-16 h-16 rounded-full object-cover border-4 border-gray-100"
                    loading="lazy"
                  />
                  <div className="text-left">
                    <p className="font-bold text-gray-900 text-lg">
                      {testimonials[currentTestimonial].name}
                    </p>
                    <p className="text-[#2D869C] font-semibold">{testimonials[currentTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center mt-8 gap-2">
              {testimonials.map((_, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={`Show testimonial ${idx + 1}`}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === currentTestimonial
                      ? 'w-8 bg-[#2D869C]'
                      : 'w-2 bg-gray-300 hover:bg-[#2D869C]/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-20 lg:py-24 bg-white"
        aria-label="Contact AvdCare"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4">
              <span className="text-[#2D869C] font-semibold text-sm uppercase tracking-wider">
                Get In Touch
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
              Contact Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                We'd Love to Hear From You
              </h3>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Have questions? We're here to help. Contact us today to learn more
                about our services.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: '📍',
                    title: 'Visit Us',
                    content: 'B/118, Mandpeswar Industrial Estates, Borivali - West, 400092, Mumbai',
                  },
                  {
                    icon: '📞',
                    title: 'Call Us',
                    content: '022-40709812',
                  },
                  {
                    icon: '✉️',
                    title: 'Email Us',
                    content: 'office@avdinovex.ai',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#2D869C]/10 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-600">{item.content}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Send us a Message
              </h3>
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  alert('Message sent! Thank you.');
                  e.target.reset();
                }}
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input
                    type="text"
                    placeholder="First Name"
                    name="firstName"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-gray-900 bg-white transition-all"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    name="lastName"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-gray-900 bg-white transition-all"
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email Address"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-gray-900 bg-white transition-all"
                />
                <textarea
                  placeholder="Your Message"
                  rows={5}
                  name="message"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-gray-900 bg-white resize-y transition-all"
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white py-4 rounded-xl hover:shadow-lg transition duration-300 font-semibold"
                >
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <Logo size="md" showText={true} className="mb-4" />
              <p className="text-gray-400 leading-relaxed text-base mb-6 max-w-md">
                Providing world-class healthcare services with compassion and innovation since day one.
              </p>
            </div>

            <div className="text-left md:text-right">
              <h4 className="font-bold mb-3 text-gray-300 uppercase text-sm tracking-wider">
                Emergency
              </h4>
              <div className="text-red-500 text-4xl font-black mb-2">
                911
              </div>
              <p className="text-gray-400">For medical emergencies</p>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-10 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              &copy; 2026 AvdCare. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      </div>
      {/* Floating WhatsApp Button */}
      <motion.a
        href="https://wa.me/918080040672?text=Hello%21%20I%27d%20like%20to%20book%20an%20appointment%20at%20AvdCare.%20Could%20you%20please%20help%20me%3F"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center"
        style={{ backgroundColor: '#25D366' }}
      >
        {/* WhatsApp SVG */}
        <svg viewBox="0 0 32 32" className="w-8 h-8 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.003 2.667C8.639 2.667 2.667 8.638 2.667 16c0 2.35.636 4.653 1.843 6.669L2.667 29.333l6.85-1.796A13.279 13.279 0 0 0 16.003 29.333C23.364 29.333 29.333 23.362 29.333 16S23.364 2.667 16.003 2.667Zm0 2.4c5.92 0 10.933 5.013 10.933 10.933S21.923 26.933 16.003 26.933a10.9 10.9 0 0 1-5.565-1.524l-.398-.24-4.065 1.066 1.086-3.953-.264-.41A10.895 10.895 0 0 1 5.07 16C5.07 10.08 10.083 5.067 16.003 5.067Zm-3.42 4.8c-.21 0-.55.079-.838.394-.288.315-1.099 1.074-1.099 2.618 0 1.544 1.125 3.036 1.282 3.245.156.209 2.193 3.465 5.384 4.717 2.523.992 3.035.794 3.58.744.547-.05 1.766-.722 2.015-1.42.25-.698.25-1.296.175-1.42-.076-.124-.285-.199-.597-.348-.312-.15-1.845-.908-2.131-1.013-.287-.104-.495-.155-.704.156-.21.31-.81 1.013-.992 1.221-.183.209-.365.234-.677.078-.312-.156-1.318-.486-2.511-1.55-.928-.828-1.555-1.849-1.737-2.161-.183-.312-.02-.48.137-.635.14-.14.312-.364.468-.547.156-.183.208-.312.313-.521.104-.21.052-.394-.026-.547-.078-.156-.69-1.694-.958-2.322-.246-.59-.5-.51-.703-.52l-.598-.01a1.14 1.14 0 0 0-.83.39Z"/>
        </svg>
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: '#25D366' }} />
      </motion.a>

      {/* Appointment Booking Modal */}
      <AnimatePresence>
        {showBooking && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-modal="true"
            role="dialog"
            aria-labelledby="booking-modal-title"
            tabIndex={-1}
            onClick={() => setShowBooking(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full relative p-8 max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              role="document"
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowBooking(false)}
                className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition z-10 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
                aria-label="Close appointment booking"
              >
                <XMarkIcon className="w-6 h-6" />
              </motion.button>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#2D869C] to-[#9DC9D4] rounded-xl flex items-center justify-center shadow-lg">
                  <CalendarIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2
                    id="booking-modal-title"
                    className="text-2xl font-black text-gray-900"
                  >
                    Book an Appointment
                  </h2>
                  <p className="text-sm text-gray-600">Fill in your details to schedule your visit</p>
                </div>
              </div>
              
              <PublicBookingForm isOpen={showBooking} onClose={() => setShowBooking(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
